import { Link, useLocation } from "react-router-dom";
import { Menu, X, Music, Music2, Music3, Music4 } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

export default function Navbar() {
  const location = useLocation();
  const { admin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/store", label: "Store" },
    { path: "/reviews", label: "Reviews" },
    { path: "/licensing", label: "Licensing" },
    { path: "/contact", label: "Contact" },
    ...(admin ? [{ path: "/admin", label: "Admin" }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <div className="relative w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
              <Music className="absolute -top-1 -left-1 w-3 h-3 text-primary opacity-70" />
              <Music2 className="absolute -top-1 -right-1 w-3 h-3 text-secondary opacity-70" />
              <span className="font-heading font-black text-lg text-primary">SM</span>
              <Music3 className="absolute -bottom-1 -left-1 w-3 h-3 text-secondary opacity-70" />
              <Music4 className="absolute -bottom-1 -right-1 w-3 h-3 text-primary opacity-70" />
            </div>
            <span className="font-heading font-bold text-xl tracking-tight">
              <span className="italic text-primary">Truu</span>
              <span className="text-foreground"> Production Lane</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <Button
                  variant={isActive(link.path) ? "default" : "ghost"}
                  className={`rounded-full px-6 font-medium transition-all ${
                    isActive(link.path)
                      ? "bg-primary text-white"
                      : "hover:bg-white/5"
                  }`}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/10"
          >
            <div className="px-6 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  <div
                    className={`px-4 py-3 rounded-xl font-medium transition-all ${
                      isActive(link.path)
                        ? "bg-primary text-white"
                        : "hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
